// ── PlanCard (issue #780) ──────────────────────────────────────────────────
// Renders the tenant's current plan on /settings so every user — not just
// partners viewing the billing portal — can see what they're paying for and
// what they'd unlock by upgrading. Closes the UX gap where subscription
// tier was set at signup but never surfaced anywhere in the regular UI.
//
// Visible to every authenticated user. Pulls the tier directly from the
// `MyTenant` payload that Settings already loads, so there's no extra
// API roundtrip — all the data is already in memory.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPlanDisplayName, getUpgradeTarget } from '@blueprint/shared';
import { getPricingDisplay } from '../lib/plan-card';
import i18n from '../i18n';
import type { MyTenant } from '../hooks/useMyTenant';
import TierFeaturesPopover from './TierFeaturesPopover';

const cardStyle: React.CSSProperties = {
  background:    'var(--surface)',
  border:        '1px solid var(--border)',
  borderRadius:  'var(--radius-lg)',
  boxShadow:     'var(--shadow-sm)',
  padding:       24,
  marginBottom:  16,
};

const labelStyle: React.CSSProperties = {
  fontFamily:     'var(--font-body)',
  fontSize:       12,
  fontWeight:     500,
  color:          'var(--text-secondary)',
  textTransform:  'uppercase',
  letterSpacing:  0.4,
  marginBottom:   4,
};

const planNameStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize:   24,
  fontWeight: 700,
  color:      'var(--text)',
  lineHeight: 1.1,
};

const tierBadgeStyle: React.CSSProperties = {
  display:       'inline-block',
  padding:       '4px 10px',
  fontFamily:    'var(--font-body)',
  fontSize:      12,
  fontWeight:    600,
  color:         'var(--primary)',
  background:    'rgba(124,58,237,0.1)',
  border:        '1px solid var(--primary)',
  borderRadius:  'var(--radius-full)',
};

const upgradeBtnStyle: React.CSSProperties = {
  display:        'inline-block',
  padding:        '8px 16px',
  fontFamily:     'var(--font-display)',
  fontSize:       13,
  fontWeight:     600,
  color:          'white',
  background:     'var(--primary)',
  border:         'none',
  borderRadius:   'var(--radius-sm)',
  textDecoration: 'none',
  transition:     'box-shadow 150ms',
};

// Style for the info icon button that opens the tier-features popover
// (issue #786). Sits inline with the plan name; small, subtle, but
// keyboard- and screen-reader-friendly.
const infoIconBtnStyle: React.CSSProperties = {
  display:        'inline-flex',
  alignItems:     'center',
  justifyContent: 'center',
  width:          24,
  height:         24,
  padding:        0,
  fontFamily:     'var(--font-body)',
  fontSize:       14,
  fontWeight:     700,
  color:          'var(--text-secondary)',
  background:     'transparent',
  border:         '1px solid var(--border)',
  borderRadius:   '50%',
  cursor:         'pointer',
  lineHeight:     1,
};

interface PlanCardProps {
  /** From useMyTenant(). When null (loading / unauthenticated) the card renders nothing. */
  tenant: MyTenant | null;
}

export default function PlanCard({ tenant }: PlanCardProps) {
  const [showFeatures, setShowFeatures] = useState(false);
  const infoIconRef = useRef<HTMLButtonElement>(null);

  if (!tenant) return null;

  const tier        = tenant.subscriptionTier;
  const planName    = getPlanDisplayName(tier);
  const upgradeTo   = getUpgradeTarget(tier);
  const pricing     = getPricingDisplay(tier, {
    locale:   i18n.language,
    timezone: tenant.timezone ?? null,
  });

  return (
    <section style={cardStyle} data-testid="panel-plan">
      <div style={labelStyle}>Plan</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: pricing.label ? 12 : 0, position: 'relative' }}>
        <span style={planNameStyle} data-testid="plan-card-name">{planName}</span>
        {tier && <span style={tierBadgeStyle} data-testid="plan-card-tier">{tier}</span>}
        <button
          ref={infoIconRef}
          type="button"
          onClick={() => setShowFeatures((s) => !s)}
          style={infoIconBtnStyle}
          aria-label={`View ${planName} plan features`}
          aria-expanded={showFeatures}
          aria-haspopup="dialog"
          title="View plan features"
          data-testid="plan-card-info-icon"
        >
          ⓘ
        </button>
        {showFeatures && (
          <TierFeaturesPopover
            tier={tier}
            triggerRef={infoIconRef}
            onClose={() => setShowFeatures(false)}
          />
        )}
      </div>

      {pricing.label && (
        <div
          data-testid="plan-card-pricing"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize:   pricing.numeric ? 16 : 14,
            fontWeight: pricing.numeric ? 600 : 500,
            color:      pricing.numeric ? 'var(--text)' : 'var(--text-secondary)',
          }}
        >
          {pricing.label}
        </div>
      )}

      {upgradeTo && (
        <div style={{ marginTop: 16 }}>
          {/* /upgrade matches the existing convention used by the
              SettingsOrganisationPage upgrade modal. If the route doesn't
              exist yet, the user lands on the app router 404 — better
              than a broken/no-op CTA. */}
          <Link
            to="/upgrade"
            style={upgradeBtnStyle}
            data-testid="plan-card-upgrade-cta"
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
          >
            Upgrade to {getPlanDisplayName(upgradeTo)} →
          </Link>
        </div>
      )}

      {tenant.isPartner && tier === 'PLATFORM_PARTNER' && (
        <div style={{ marginTop: 16 }}>
          {/* Partner-agreement details (seats / usage / invoices) live
              on the deep-dive billing portal. PlanCard summarises;
              BillingPage handles the long form. */}
          <Link
            to="/settings/billing"
            style={{ ...upgradeBtnStyle, background: 'transparent', color: 'var(--primary)', border: '1px solid var(--primary)' }}
            data-testid="plan-card-billing-link"
          >
            View billing details →
          </Link>
        </div>
      )}
    </section>
  );
}
